/**
 * A loading screen's line from the field, typed out (the user, 2026-10-07):
 * typed letter by letter, held long enough to read, deleted the same way,
 * then another — picked at random, never the same one twice running.
 *
 * Where the lines come from is the app's business (`app/loadingLines.ts`,
 * `GET /loading-lines`, kept on the device); `src/ui` builds alone, so they
 * arrive through `LoadingLinesProvider`. Without one — another host of the
 * design system, a test — the screen's own built-in lines are used.
 *
 * Inside a rave about half the lines are THAT rave's ("Your OZORA experience
 * is waiting in heaven…"): `rave` is a separate pool so a short one is not
 * drowned out by two hundred general lines.
 *
 * Decoration, so `aria-hidden` — the screen's `label` says what is happening.
 * Less motion asked for: no typing, a whole line swapped every few seconds.
 */
import { createContext, useContext, useEffect, useRef, useState } from 'react';

export interface LoadingLinePool {
  general: readonly string[];
  /** This rave's lines, its name already in them. Empty outside a rave. */
  rave: readonly string[];
  /**
   * Lines greeting the signed-in person by first name (step 98), the name already in them — the FIRST line a
   * screen types, then one in two. Empty signed out, or with no first name.
   */
  name?: readonly string[];
  /** The first name inside those lines — drawn bold and a little larger (the user, 2026-11-02). */
  who?: string;
}

const LinesContext = createContext<LoadingLinePool | null>(null);
export const LoadingLinesProvider = LinesContext.Provider;

/** After the first line, the share of lines that greet by name (2026-10-30: one in three → one in two). */
export const NAME_SHARE = 1 / 2;
const TYPE_MS = 38;
/** How long a typed line stays whole (the user, 2026-11-02: "it changes too fast, add 2 more sec for each line" — 2.6 s → 4.6 s). */
export const HOLD_MS = 4600;
const ERASE_MS = 16;
const GAP_MS = 350;
/** Less motion: a whole line swapped this often — also 2 s longer (2026-11-02). */
export const STILL_MS = 6500;

/**
 * `first`: the screen's opening line. Signed in, it is ALWAYS a name line (the user, 2026-10-29: "I don't see it" —
 * at one in three, on a screen up for a second or two, the greeting almost never came); after it, one in two (2026-10-30: "I saw my name 2 times out of 20 — make it more").
 */
function pick(pool: LoadingLinePool, previous: string | null, first = false): string {
  const named = pool.name ?? [];
  const from = named.length > 0 && (first || Math.random() < NAME_SHARE) ? named
    : pool.rave.length > 0 && Math.random() < 0.5 ? pool.rave : pool.general;
  const choices = from.length > 1 ? from.filter((l) => l !== previous) : from;
  return choices[Math.floor(Math.random() * choices.length)] ?? '';
}

const reducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function TypedLine({ fallback }: { fallback: readonly string[] }) {
  const provided = useContext(LinesContext);
  const pool: LoadingLinePool = provided && provided.general.length > 0
    ? provided
    : { general: fallback, rave: [] };
  // The pool can change under a running line (the list arrives from the
  // network); the loop reads the latest without restarting.
  const poolRef = useRef(pool);
  poolRef.current = pool;

  const [line, setLine] = useState(() => pick(pool, null, true));
  const [shown, setShown] = useState(() => (reducedMotion() ? line.length : 0));
  const [phase, setPhase] = useState<'type' | 'hold' | 'erase'>(() => (reducedMotion() ? 'hold' : 'type'));

  useEffect(() => {
    const still = reducedMotion();
    let t: ReturnType<typeof setTimeout>;
    if (still) {
      t = setTimeout(() => {
        const next = pick(poolRef.current, line);
        setLine(next); setShown(next.length);
      }, STILL_MS);
    } else if (phase === 'type') {
      t = shown < line.length
        ? setTimeout(() => setShown(shown + 1), TYPE_MS)
        : setTimeout(() => setPhase('hold'), 0);
    } else if (phase === 'hold') {
      t = setTimeout(() => setPhase('erase'), HOLD_MS);
    } else {
      t = shown > 0
        ? setTimeout(() => setShown(shown - 1), ERASE_MS)
        : setTimeout(() => { setLine(pick(poolRef.current, line)); setPhase('type'); }, GAP_MS);
    }
    return () => clearTimeout(t);
  }, [line, shown, phase]);

  // The person's name, as far as it is typed, bold and a little larger (the user, 2026-11-02).
  const who = pool.who ?? '';
  const at = who ? line.indexOf(who) : -1;
  const typed = line.slice(0, shown);
  return (
    <span aria-hidden="true" className="ui-wait-line" data-phase={phase}>
      {at < 0 || shown <= at ? typed : (
        <>
          {typed.slice(0, at)}
          <b className="ui-wait-name">{typed.slice(at, at + who.length)}</b>
          {typed.slice(at + who.length)}
        </>
      )}
      <span className="ui-caret" />
    </span>
  );
}
